/**
 * BottomNav Component — Soorgaa Design System
 * 
 * Mobile-first primary navigation bar (visible on <1024px, hidden on desktop).
 * Displays 5 core tabs with 44px+ hit targets, active state indicator,
 * and safe-area insets.
 */
import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  BookOpen,
  GraduationCap,
  Download,
  User,
} from "lucide-react";

interface NavItem {
  name: string;
  to: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  exact?: boolean;
}

const navItems: NavItem[] = [
  { name: "Dashboard", to: "/student", icon: LayoutDashboard, exact: true },
  { name: "Study", to: "/student/study", icon: BookOpen },
  { name: "Practice", to: "/student/coc", icon: GraduationCap },
  { name: "Downloads", to: "/student/downloads", icon: Download },
  { name: "Profile", to: "/student/profile", icon: User },
];

export function BottomNav() {
  const location = useLocation();

  return (
    <nav
      aria-label="Mobile Navigation"
      className="lg:hidden fixed left-3 right-3 bottom-[calc(0.75rem+env(safe-area-inset-bottom))] z-40 glass-overlay border border-border rounded-bar shadow-lg"
    >
      <div className="grid grid-cols-5 h-16 items-center max-w-lg mx-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = item.exact
            ? location.pathname === item.to
            : location.pathname.startsWith(item.to);

          return (
            <NavLink
              key={item.name}
              to={item.to}
              end={item.exact}
              aria-current={isActive ? "page" : undefined}
              className={`relative flex flex-col items-center justify-center h-full px-1 py-1 transition-colors select-none cursor-pointer ${
                isActive
                  ? "text-primary font-bold"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {isActive && (
                <div className="absolute inset-x-1.5 inset-y-1.5 rounded-key bg-accent-soft" aria-hidden="true" />
              )}

              <Icon size={20} className="relative" />
              <span className="relative text-xs tracking-tight mt-0.5 truncate max-w-full">
                {item.name}
              </span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
